import React from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Image as ImageIcon, Type, UploadCloud, Image as ImagePlaceholder, Check, Phone, Loader2 } from "lucide-react";
import { Input } from "@shared/ui/input";
import { AuditTrailModal } from './AuditTrailModal';
import { useAddSelfServieBranding, useUpdateSelfServieBranding } from '../../hooks';

const PortalBranding = ({ mode, setMode, text, setText, logoFile, setLogoFile, onReset, remoteUtilityId, isPristine, existingLogoUrl, existingId }: any) => {
  const { mutate: addBranding, isPending: isAdding } = useAddSelfServieBranding();
  const { mutate: updateBranding, isPending: isUpdating } = useUpdateSelfServieBranding();
  
  const isPending = isAdding || isUpdating;
  const [isEditing, setIsEditing] = React.useState(!existingId);

  React.useEffect(() => {
    setIsEditing(!existingId);
  }, [existingId]);

  const handleSave = () => {
    const formData = new FormData();
    formData.append('branding_type', '1');
    formData.append('remote_utility_id', remoteUtilityId);
    
    if (mode === 'logo') {
      formData.append('branding_field_type', '0');
      if (logoFile) {
        formData.append('logo', logoFile);
      }
    } else {
      formData.append('branding_field_type', '1');
      if (text) {
        formData.append('brand_text', text);
      }
    }

    if (existingId) {
      updateBranding({ id: existingId, payload: formData });
    } else {
      addBranding(formData);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Settings Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Portal Branding</h2>
          <p className="text-sm text-gray-500 mb-6">
            Choose how the consumer portal header is branded — with your utility logo image, or with a text title. Changes apply to the public Self Service portal.
          </p>

          <div className={!isEditing ? 'pointer-events-none opacity-60' : ''}>
            <div className="grid grid-cols-2 gap-4 mb-6">
            <div 
              className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${mode === 'logo' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              onClick={() => setMode('logo')}
            >
              <div className={`p-2 rounded bg-blue-500 text-white`}>
                <ImageIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">Logo Image</div>
                <div className="text-[10px] text-gray-500">Upload a PNG, JPG, or SVG (recommended 240x60px).</div>
              </div>
              <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${mode === 'logo' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {mode === 'logo' && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>

            <div 
              className={`border rounded-lg p-4 cursor-pointer flex items-center gap-3 transition-colors ${mode === 'text' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              onClick={() => setMode('text')}
            >
              <div className={`p-2 rounded bg-blue-500 text-white`}>
                <Type className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-gray-900">Text Only</div>
                <div className="text-[10px] text-gray-500">Display the utility name as styled text.</div>
              </div>
              <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${mode === 'text' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {mode === 'text' && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
          </div>

          {mode === 'logo' && !logoFile && (
            <label htmlFor="portal-logo-upload" className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 mb-6 transition-all hover:bg-gray-50 cursor-pointer text-center w-full block">
              <input type="file" id="portal-logo-upload" className="hidden" accept="image/png, image/jpeg, image/webp" onChange={(e) => { if(e.target.files?.length) setLogoFile(e.target.files[0]); }} />
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 mx-auto">
                <UploadCloud className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">Click to upload logo</p>
              <p className="text-xs text-gray-500">PNG, JPG, or SVG up to 2 MB</p>
            </label>
          )}

          {mode === 'logo' && logoFile && (
            <div className="border border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center bg-gray-50/50 mb-6 w-full">
              <div className="w-full flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                     <ImagePlaceholder className="w-6 h-6" />
                   </div>
                   <div>
                     <p className="text-sm font-bold text-gray-900 truncate max-w-[200px]">{logoFile.name}</p>
                     <p className="text-xs text-gray-500">{(logoFile.size / 1024).toFixed(1)} KB</p>
                   </div>
                </div>
                <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 text-sm" onClick={() => setLogoFile(null)}>
                  Remove
                </Button>
              </div>
            </div>
          )}

          {mode === 'text' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-1">Brand Text</label>
              <Input value={text} onChange={(e) => setText(e.target.value)} className="w-full" placeholder="Enter brand name..." />
              <p className="text-xs text-gray-500 mt-1.5">Shown in the portal header in place of a logo.</p>
            </div>
          )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <AuditTrailModal 
              title="Portal Branding" 
              remoteUtilityId={remoteUtilityId} 
              actionType="PORTAL_BRANDING_UPDATE" 
            />

            <Button variant="outline" className="text-gray-600 gap-2" onClick={!isEditing ? () => setIsEditing(true) : onReset} disabled={isPending}>
              {!isEditing ? "Edit" : "Reset"}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSave} disabled={isPending || isPristine || !isEditing}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isPending ? "Saving..." : "Save Branding"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Live Preview</h2>
          <p className="text-sm text-gray-500 mb-6">See how the branding will appear at the top of the Self Service consumer portal.</p>
        
          <div className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-3 border-b border-gray-100">
              CONSUMER PORTAL HEADER
            </div>
            
            {/* The Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
              <div>
                {mode === 'logo' ? (
                  logoFile || existingLogoUrl ? (
                    <div className="flex items-center gap-2 bg-white px-1 py-1 rounded">
                      <img src={logoFile ? URL.createObjectURL(logoFile) : existingLogoUrl} alt="Logo" className="max-h-8 max-w-[120px] object-contain" />
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No logo uploaded</span>
                  )
                ) : (
                  <span className="text-lg font-bold">{text || 'Your Logo'}</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-[11px] font-semibold text-gray-300">
                <span>Bills</span>
                <span>Services</span>
                <span>Support</span>
              </div>
            </div>

            {/* The Body Placeholder */}
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center min-h-[160px]">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome to your utility portal</h3>
              <p className="text-sm text-gray-500">Pay bills, raise complaints, and manage your services online.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalBranding;
